import { StorageSerializers, useStorage } from '@vueuse/core';
import { InferEnum } from 'shared/types';
import { computed, type ComputedRef } from 'vue';
import { z } from 'zod';

export const checkoutHashStatus = {
  active: 'active',
  ended: 'ended',
  expired: 'expired',
  canceled: 'canceled',
} as const;
export type ICheckoutHashStatus = InferEnum<typeof checkoutHashStatus>;
export const allCheckoutStatus = Object.values(
  checkoutHashStatus
) as ICheckoutHashStatus[];
const statusEnum = z.enum(allCheckoutStatus);

const CheckoutHashSetSchema = z.object({
  /**
   * timestamp of creation
   */
  c: z.number().int().nonnegative(),
  /** location id */
  lid: z.number().int().nonnegative(),
  /** session hash */
  h: z.string().min(1),
  /** status of the hash */
  s: statusEnum.optional(),
});

export type ICheckoutHashEmpty = null;

/**
 * Keeping the payload small because it is stored in the Local Storage, and we try to save some bytes.
 */
export type ICheckoutHashSet = z.infer<typeof CheckoutHashSetSchema>;

export type ICheckoutHash = ICheckoutHashEmpty | ICheckoutHashSet;

const EMPTY: ICheckoutHashEmpty = null;
const NAME = 'checkout-session';

const checkoutHash = useStorage<ICheckoutHash | null>(
  NAME,
  EMPTY,
  localStorage,
  {
    serializer: StorageSerializers.object,
  }
);

function isSet(value: ICheckoutHash): value is ICheckoutHashSet {
  const res = CheckoutHashSetSchema.safeParse(value);
  return res.success;
}

function isEmpty(value: ICheckoutHash): value is ICheckoutHashEmpty {
  return !isSet(value);
}

export interface IUseCheckoutHashReturnType {
  set: (locationId: number, sessionHash: string) => void;
  invalidate: (reason: ICheckoutHashStatus) => void;
  status: Readonly<ComputedRef<ICheckoutHashStatus | null>>;
  clear: () => void;
  isSet: Readonly<ComputedRef<boolean>>;
  isEmpty: Readonly<ComputedRef<boolean>>;
  isActive: Readonly<ComputedRef<boolean>>;
  locationId: Readonly<ComputedRef<number | null>>;
  hash: Readonly<ComputedRef<string | null>>;
  raw: Readonly<ComputedRef<ICheckoutHash | null>>;
}

export function useCheckoutHash(): IUseCheckoutHashReturnType {
  function set(locationId: number, sessionHash: string) {
    checkoutHash.value = {
      lid: locationId,
      h: sessionHash,
      c: Date.now(),
      s: checkoutHashStatus.active,
    };
  }

  function invalidate(reason: ICheckoutHashStatus) {
    const existingValue = checkoutHash.value;

    if (isSet(existingValue)) {
      checkoutHash.value = {
        ...existingValue,
        s: reason,
      };
    }
  }

  function clear() {
    checkoutHash.value = EMPTY;
  }

  const isEmptyRef = computed(() => isEmpty(checkoutHash.value || EMPTY));
  const isSetRef = computed(() => isSet(checkoutHash.value || EMPTY));
  const statusRef = computed(() => checkoutHash.value?.s || null);
  const isActiveRef = computed(
    () => statusRef.value === checkoutHashStatus.active
  );

  return {
    invalidate,
    set,
    clear,
    isSet: isSetRef,
    isEmpty: isEmptyRef,
    isActive: isActiveRef,
    status: statusRef,
    locationId: computed(() => {
      return isSetRef.value
        ? (checkoutHash.value as ICheckoutHashSet).lid
        : null;
    }),
    raw: computed(() => {
      if (!checkoutHash.value || isEmptyRef.value) {
        return null;
      }

      return checkoutHash.value;
    }),
    hash: computed(() => {
      return isSetRef.value ? (checkoutHash.value as ICheckoutHashSet).h : null;
    }),
  };
}
